import logger from '../utils/logger';
/**
 * STREAMING UPLOAD v6 — Parallel PutObject (Dropbox/Drive style)
 *
 * WHY SINGLE-PUT WAS SLOW:
 * One HTTP/2 stream to Scaleway S3 = ~800 KB/s (flow control limit per stream).
 * User has 10 MB/s upload (proven via Google Drive) but only 8% is utilized.
 *
 * SOLUTION:
 * Split file into 8 parts → 8 separate PutObject requests (separate S3 keys)
 * → 8 parallel XHR streams → 8 × 800 KB/s = ~6.4 MB/s
 * → Server assembles via UploadPartCopy (instant, server-side S3 copy)
 *
 * Architecture:
 *   Browser: 8 XHR PUT ──────▸ S3 (8 temp keys)
 *   Server:  UploadPartCopy ──▸ S3 (1 final key) → DuckDB processing
 */

import { API_URL } from './config';
import { authFetch } from '@/utils/api-headers';
import { supabase } from './supabase';
import { useUploadStore, type UploadSessionState } from '@/stores/upload-store';

// ───────────── Configuration ─────────────

const PARALLEL_PARTS = 8;              // 8 parallel streams
const MIN_PART_SIZE = 5 * 1024 * 1024; // S3 minimum for UploadPartCopy
const MAX_SINGLE_PUT = 5 * 1024 * 1024 * 1024;
const MAX_RETRIES = 3;

// ───────────── Types ─────────────

export interface UploadProgress {
  totalChunks: number;
  uploadedChunks: number;
  percentage: number;
  bytesUploaded: number;
  totalBytes: number;
  uploadSpeed: number;
  estimatedTimeRemaining: number;
}

export interface StreamingUploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  onChunkComplete?: (chunkIndex: number) => void;
  onError?: (error: Error) => void;
  onPaused?: () => void;
  onResumed?: () => void;
}

interface PartInfo {
  partIndex: number;
  partKey: string;
  presignedUrl: string;
  start: number;
  end: number;
  size: number;
}

// ───────────── StreamingUploader ─────────────

class StreamingUploader {
  private s3Key: string | null = null;
  private datasetId: string | null = null;
  private startTime: number = 0;
  private isPaused: boolean = false;
  private isCancelled: boolean = false;
  private activeXHRs: XMLHttpRequest[] = [];
  private userId: string | null = null;

  // Per-part progress tracking
  private partBytesLoaded: number[] = [];

  // ───── UPLOAD ─────

  async upload(
    file: File,
    userId: string,
    options: StreamingUploadOptions = {}
  ): Promise<{ uploadId: string; s3Key: string }> {
    this.userId = userId;
    this.startTime = Date.now();
    this.isPaused = false;
    this.isCancelled = false;
    this.activeXHRs = [];

    if (file.size > MAX_SINGLE_PUT) {
      throw new Error(`File too large (${(file.size / 1024 / 1024 / 1024).toFixed(1)} GB). Max 5 GB.`);
    }

    const numParts = file.size > 100 * 1024 * 1024 ? PARALLEL_PARTS : 1;

    if (numParts === 1) {
      return this.uploadSinglePut(file, userId, options);
    }

    return this.uploadParallel(file, userId, numParts, options);
  }

  // ═══════════════════════════════════════════════════════════════
  // STRATEGY: Parallel PutObject (8 simultaneous XHR streams)
  // ═══════════════════════════════════════════════════════════════

  private async uploadParallel(
    file: File,
    userId: string,
    numParts: number,
    options: StreamingUploadOptions
  ): Promise<{ uploadId: string; s3Key: string }> {
    logger.log(`[Upload] PARALLEL ${numParts}×: ${file.name} (${(file.size / 1024 / 1024).toFixed(0)} MB)`);

    // 1. Get presigned URLs for all parts
    const initData = await this.parallelInit(file.name, userId, file.size, numParts);
    const { finalKey, parts } = initData;
    this.s3Key = finalKey;

    const pseudoUploadId = `parallel_${Date.now()}`;

    // 2. Create dataset row
    try {
      const { data: dsData } = await supabase
        .from('big_data_datasets')
        .insert({
          user_id: userId,
          file_name: file.name,
          file_size: file.size,
          s3_key: finalKey,
          s3_bucket: 'clairefy-uploads-prod-v2',
          job_id: `upload_${pseudoUploadId}`,
          status: 'uploading',
          upload_status: 'uploading',
          total_chunks: numParts,
          uploaded_chunks: 0,
          completed_parts: [],
          bytes_uploaded: 0,
        })
        .select('id')
        .single();
      if (dsData) this.datasetId = dsData.id;
    } catch (error) {
      logger.error(`[Upload] Dataset row failed:`, error);
    }

    // 3. Register in Zustand
    try {
      useUploadStore.getState().registerUpload({
        datasetId: this.datasetId || undefined,
        uploadId: pseudoUploadId, s3Key: finalKey,
        fileName: file.name, fileSize: file.size, chunkSize: 0, totalChunks: numParts,
        uploadedChunks: 0, completedParts: [],
        uploadStatus: 'uploading', bytesUploaded: 0,
        uploadSpeed: 0, estimatedTimeRemaining: 0,
        createdAt: new Date().toISOString(),
      });
    } catch { /* non-blocking */ }

    // 4. Upload all parts in PARALLEL via XHR
    this.partBytesLoaded = new Array(parts.length).fill(0);
    let completedParts = 0;

    const reportProgress = () => {
      const totalLoaded = this.partBytesLoaded.reduce((a, b) => a + b, 0);
      const elapsed = (Date.now() - this.startTime) / 1000;
      const speed = elapsed > 0 ? totalLoaded / elapsed : 0;
      const remaining = speed > 0 ? (file.size - totalLoaded) / speed : 0;
      const pct = Math.round((totalLoaded / file.size) * 100);

      try {
        useUploadStore.getState().updateProgress(pseudoUploadId, {
          uploadedChunks: completedParts,
          bytesUploaded: totalLoaded, uploadSpeed: speed,
          estimatedTimeRemaining: remaining,
        });
      } catch { /* non-blocking */ }

      if (options.onProgress) {
        options.onProgress({
          totalChunks: numParts, uploadedChunks: completedParts,
          percentage: pct, bytesUploaded: totalLoaded, totalBytes: file.size,
          uploadSpeed: speed, estimatedTimeRemaining: remaining,
        });
      }
    };

    // Fire all uploads simultaneously
    const uploadPromises = parts.map((part) => {
      const blob = file.slice(part.start, part.end);
      return this.xhrPutWithRetry(part.presignedUrl, blob, part.partIndex, (loaded) => {
        this.partBytesLoaded[part.partIndex] = loaded;
        reportProgress();
      }).then(() => {
        completedParts++;
        logger.log(`[Upload] Part ${part.partIndex + 1}/${numParts} ✓`);
        if (options.onChunkComplete) options.onChunkComplete(part.partIndex);
        reportProgress();
      });
    });

    // Wait for ALL parts to finish
    const results = await Promise.allSettled(uploadPromises);
    const failures = results.filter(r => r.status === 'rejected');

    if (failures.length > 0) {
      const errMsg = `${failures.length}/${numParts} parts failed`;
      logger.error(`[Upload] ${errMsg}`);
      // If most parts succeeded, try single PUT fallback
      if (failures.length === numParts) {
        logger.warn(`[Upload] All parts failed, trying single PUT fallback`);
        return this.uploadSinglePut(file, userId, options);
      }
      throw new Error(errMsg);
    }

    // 5. Server-side assembly (instant: S3 internal copy)
    logger.log(`[Upload] All ${numParts} parts uploaded, assembling...`);
    const partKeys = parts.map(p => p.partKey);
    const result = await this.parallelComplete(finalKey, partKeys, userId, file.name, file.size);

    // 6. Update state
    try {
      if (this.datasetId) {
        await supabase.from('big_data_datasets')
          .update({ upload_status: 'completed', uploaded_chunks: numParts, bytes_uploaded: file.size })
          .eq('id', this.datasetId);
      }
      useUploadStore.getState().markCompleted(pseudoUploadId);
    } catch { /* non-blocking */ }

    const totalTime = ((Date.now() - this.startTime) / 1000).toFixed(0);
    const avgSpeed = ((file.size / 1024 / 1024) / Math.max(parseFloat(totalTime), 1)).toFixed(1);
    logger.log(`[Upload] COMPLETE in ${totalTime}s (${avgSpeed} MB/s): ${result.jobId}`);
    return { uploadId: result.jobId, s3Key: finalKey };
  }

  // ═══════════════════════════════════════════════════════════════
  // FALLBACK: Single PutObject (for small files or if parallel fails)
  // ═══════════════════════════════════════════════════════════════

  private async uploadSinglePut(
    file: File,
    userId: string,
    options: StreamingUploadOptions
  ): Promise<{ uploadId: string; s3Key: string }> {
    logger.log(`[Upload] Single PUT: ${file.name} (${(file.size / 1024 / 1024).toFixed(0)} MB)`);

    const resp = await authFetch(`${API_URL}/api/big-data-server/upload/presigned-put`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName: file.name, userId, fileSize: file.size })
    });
    if (!resp.ok) throw new Error('Failed to get presigned PUT URL');
    const { presignedUrl, s3Key } = await resp.json();
    this.s3Key = s3Key;

    const pseudoUploadId = `singleput_${Date.now()}`;

    try {
      const { data: dsData } = await supabase
        .from('big_data_datasets')
        .insert({
          user_id: userId, file_name: file.name, file_size: file.size,
          s3_key: s3Key, s3_bucket: 'clairefy-uploads-prod-v2',
          job_id: `upload_${pseudoUploadId}`, status: 'uploading',
          upload_status: 'uploading', total_chunks: 1,
          uploaded_chunks: 0, completed_parts: [], bytes_uploaded: 0,
        })
        .select('id').single();
      if (dsData) this.datasetId = dsData.id;
    } catch { /* non-blocking */ }

    try {
      useUploadStore.getState().registerUpload({
        datasetId: this.datasetId || undefined,
        uploadId: pseudoUploadId, s3Key,
        fileName: file.name, fileSize: file.size, chunkSize: 0, totalChunks: 1,
        uploadedChunks: 0, completedParts: [],
        uploadStatus: 'uploading', bytesUploaded: 0,
        uploadSpeed: 0, estimatedTimeRemaining: 0,
        createdAt: new Date().toISOString(),
      });
    } catch { /* non-blocking */ }

    await this.xhrPutWithRetry(presignedUrl, file, 0, (loaded) => {
      const elapsed = (Date.now() - this.startTime) / 1000;
      const speed = elapsed > 0 ? loaded / elapsed : 0;
      const remaining = speed > 0 ? (file.size - loaded) / speed : 0;

      try {
        useUploadStore.getState().updateProgress(pseudoUploadId, {
          bytesUploaded: loaded, uploadSpeed: speed, estimatedTimeRemaining: remaining,
          uploadedChunks: loaded >= file.size ? 1 : 0,
        });
      } catch { /* non-blocking */ }

      if (options.onProgress) {
        options.onProgress({
          totalChunks: 1, uploadedChunks: loaded >= file.size ? 1 : 0,
          percentage: Math.round((loaded / file.size) * 100),
          bytesUploaded: loaded, totalBytes: file.size,
          uploadSpeed: speed, estimatedTimeRemaining: remaining,
        });
      }
    });

    const result = await this.completeSinglePut(s3Key, userId, file.name, file.size);

    try {
      if (this.datasetId) {
        await supabase.from('big_data_datasets')
          .update({ upload_status: 'completed', uploaded_chunks: 1, bytes_uploaded: file.size })
          .eq('id', this.datasetId);
      }
      useUploadStore.getState().markCompleted(pseudoUploadId);
    } catch { /* non-blocking */ }

    return { uploadId: result.jobId, s3Key };
  }

  // ───── XHR PUT with retry ─────

  private async xhrPutWithRetry(
    url: string,
    data: Blob | File,
    partIndex: number,
    onProgress: (loaded: number) => void,
    retryCount = 0
  ): Promise<void> {
    try {
      await this.xhrPut(url, data, onProgress);
    } catch (error: any) {
      if (retryCount < MAX_RETRIES) {
        const backoff = Math.min(2000 * Math.pow(2, retryCount), 20000);
        logger.warn(`[Upload] Part ${partIndex} retry ${retryCount + 1}: ${error.message}`);
        await this.sleep(backoff);
        return this.xhrPutWithRetry(url, data, partIndex, onProgress, retryCount + 1);
      }
      throw error;
    }
  }

  private xhrPut(
    url: string,
    data: Blob | File,
    onProgress: (loaded: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.activeXHRs.push(xhr);

      xhr.open('PUT', url, true);
      xhr.timeout = 2 * 60 * 60 * 1000; // 2 hours

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded);
      };

      xhr.onload = () => {
        this.removeXHR(xhr);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`PUT ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => { this.removeXHR(xhr); reject(new Error('Network error')); };
      xhr.ontimeout = () => { this.removeXHR(xhr); reject(new Error('Timeout')); };
      xhr.onabort = () => { this.removeXHR(xhr); reject(new Error('Aborted')); };

      xhr.send(data);
    });
  }

  private removeXHR(xhr: XMLHttpRequest) {
    const idx = this.activeXHRs.indexOf(xhr);
    if (idx !== -1) this.activeXHRs.splice(idx, 1);
  }

  // ───── RESUME ─────

  async resume(
    session: UploadSessionState,
    file: File,
    userId: string,
    options: StreamingUploadOptions = {}
  ): Promise<{ uploadId: string; s3Key: string }> {
    logger.log(`[Upload] Resume → restarting: ${session.fileName}`);
    return this.upload(file, userId, options);
  }

  // ───── Pause / Cancel ─────

  pause(): void {
    this.isPaused = true;
    this.activeXHRs.forEach(xhr => xhr.abort());
    this.activeXHRs = [];
    logger.log(`[Upload] PAUSED — all XHRs aborted`);
  }

  unpause(): void {
    this.isPaused = false;
  }

  cancel(): void {
    this.isCancelled = true;
    this.activeXHRs.forEach(xhr => xhr.abort());
    this.activeXHRs = [];
    logger.log(`[Upload] CANCELLED`);
  }

  get paused(): boolean { return this.isPaused; }

  // ───── API calls ─────

  private async parallelInit(fileName: string, userId: string, fileSize: number, numParts: number) {
    const resp = await authFetch(`${API_URL}/api/big-data-server/upload/parallel-init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName, userId, fileSize, numParts })
    });
    if (!resp.ok) throw new Error(`parallel-init failed: ${resp.status}`);
    return resp.json() as Promise<{ finalKey: string; parts: PartInfo[] }>;
  }

  private async parallelComplete(finalKey: string, partKeys: string[], userId: string, fileName: string, fileSize: number) {
    const resp = await authFetch(`${API_URL}/api/big-data-server/upload/parallel-complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ finalKey, partKeys, userId, fileName, fileSize })
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`parallel-complete failed: ${resp.status} - ${text}`);
    }
    return resp.json() as Promise<{ jobId: string }>;
  }

  private async completeSinglePut(s3Key: string, userId: string, fileName: string, fileSize: number) {
    const resp = await authFetch(`${API_URL}/api/big-data-server/upload/complete-single`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ s3Key, userId, fileName, fileSize })
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`complete-single failed: ${resp.status} - ${text}`);
    }
    return resp.json() as Promise<{ jobId: string }>;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// ───── Custom error ─────

export class UploadPausedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadPausedError';
  }
}

// ───── Factory ─────

export const getStreamingUploader = (): StreamingUploader => {
  return new StreamingUploader();
};

export default StreamingUploader;
